import { useLayoutEffect, useState, useCallback } from "react";

const DEFAULT_STEP = 308;
const DEFAULT_PAD = 16;

/**
 * Measures slick-list padding (inline-start of the viewport) and step between
 * adjacent slides. Step uses |Δleft| so it works for both ltr and rtl flex rows.
 */
export function useServicesSliderMetrics(
  listRef,
  trackRef,
  slidesKey,
  isRtl = false
) {
  const [metrics, setMetrics] = useState({
    step: DEFAULT_STEP,
    padLeft: DEFAULT_PAD,
  });

  const measure = useCallback(() => {
    const list = listRef.current;
    const track = trackRef.current;
    if (!list || !track) return;

    const first = track.querySelector(".services-picker__item");
    if (!first) return;

    const second = first.nextElementSibling;
    const trackStyle = getComputedStyle(track);
    const gap =
      parseFloat(trackStyle.columnGap || trackStyle.gap || "0") || 0;

    const stepRaw = second
      ? second.getBoundingClientRect().left -
        first.getBoundingClientRect().left
      : first.getBoundingClientRect().width + gap;
    const step = Math.max(120, Math.abs(stepRaw) || DEFAULT_STEP);

    const listStyle = getComputedStyle(list);
    const padStart =
      parseFloat(listStyle.paddingInlineStart) ||
      (isRtl
        ? parseFloat(listStyle.paddingRight)
        : parseFloat(listStyle.paddingLeft)) ||
      DEFAULT_PAD;

    setMetrics({
      step,
      padLeft: padStart,
    });
  }, [listRef, trackRef, isRtl]);

  useLayoutEffect(() => {
    measure();
    const list = listRef.current;
    const track = trackRef.current;
    if (!list || !track) return;

    const ro = new ResizeObserver(() => measure());
    ro.observe(list);
    ro.observe(track);
    const slides = track.querySelectorAll(".services-picker__item");
    slides.forEach((el) => ro.observe(el));

    window.addEventListener("load", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("load", measure);
    };
  }, [measure, slidesKey, listRef, trackRef]);

  return metrics;
}
